import { getTenantBaseUrl } from "./config";
import { t } from "./i18n";
import { AuthSession, KrebitUser, RecipientInput, SigningDraftInput, SigningResource } from "./types";

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export class KrebitApiClient {
  private readonly baseUrl: string;

  public constructor(private readonly session: AuthSession) {
    this.baseUrl = getTenantBaseUrl(session.tenant);
  }

  public async getCurrentUser(): Promise<KrebitUser> {
    return this.request<KrebitUser>("/api/1.0/me");
  }

  public async listSignings(limit = 10): Promise<SigningResource[]> {
    return this.request<SigningResource[]>(`/api/1.0/signings?limit=${encodeURIComponent(String(limit))}`);
  }

  public async createSigning(input: SigningDraftInput): Promise<SigningResource> {
    return this.request<SigningResource>("/api/1.0/signings", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  public async getSigning(signingId: string): Promise<SigningResource> {
    return this.request<SigningResource>(`/api/1.0/signings/${encodeURIComponent(signingId)}`);
  }

  public async uploadDocument(signingId: string, pdf: Blob, filename: string): Promise<void> {
    const form = new FormData();
    form.append("selected_file", pdf, filename);

    await this.request(`/api/1.0/signings/${encodeURIComponent(signingId)}/documents`, {
      method: "POST",
      body: form,
      json: false,
    });
  }

  public async addRecipient(signingId: string, recipient: RecipientInput): Promise<void> {
    await this.request(`/api/1.0/signings/${encodeURIComponent(signingId)}/recipients`, {
      method: "POST",
      body: JSON.stringify(recipient),
    });
  }

  public async sendSigning(signingId: string): Promise<SigningResource> {
    return this.request<SigningResource>(`/api/1.0/signings/${encodeURIComponent(signingId)}/send`, {
      method: "POST",
    });
  }

  private async request<T>(
    path: string,
    options: { method?: string; body?: string | FormData; headers?: Headers; json?: boolean } = {}
  ): Promise<T> {
    const headers = new Headers(options.headers || {});
    headers.set("Accept", "application/json");
    headers.set("Authorization", `Bearer ${this.session.accessToken}`);

    if (options.json !== false && options.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    const responseBody = await this.readResponseBody(response);

    if (!response.ok) {
      throw new ApiError(this.getApiErrorMessage(responseBody, t("genericError")), response.status);
    }

    return this.unwrapResource(responseBody) as T;
  }

  private async readResponseBody(response: Response): Promise<unknown> {
    const text = await response.text();

    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (_error) {
      return { message: text };
    }
  }

  private unwrapResource(body: unknown): unknown {
    if (body && typeof body === "object" && "data" in body) {
      return (body as { data: unknown }).data;
    }

    return body;
  }

  private getApiErrorMessage(body: unknown, fallback: string): string {
    if (body && typeof body === "object") {
      const value = body as {
        message?: string;
        error?: string;
        error_description?: string;
        errors?: Record<string, string[]>;
      };

      if (value.message) {
        return value.message;
      }

      if (value.error_description || value.error) {
        return value.error_description || value.error || fallback;
      }

      if (value.errors) {
        const firstError = Object.keys(value.errors)
          .map((key) => value.errors?.[key]?.[0])
          .find(Boolean);

        if (firstError) {
          return firstError;
        }
      }
    }

    return fallback;
  }
}
